/**
 * String Registry — main page (parallel UX to Configuration Registry)
 */

import { useEffect, useCallback, useState, useRef } from "react";
import {
  Box,
  Typography,
  Badge,
  useTheme,
  Tabs,
  Tab,
  Paper
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGlobalConfig } from "context/GlobalContext";
import { BglLoading } from "components/loading";
import { BglPrimarySmallButton, BglSecondarySmallButton } from "components/button/BglButtons";
import {
  selectStringRegistryStrings,
  selectStringRegistryPagination,
  selectStringRegistryFilters,
  selectStringRegistryIsLoading,
  selectStringRegistryError,
  selectHasActiveFilters,
  setStrings,
  setPagination,
  setIsLoading,
  setError,
  setUISchema,
  toggleFilterPanel,
} from "../store/stringRegistryUISlice";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { ConfigRegistryApis } from "features/config-registry/api/configRegistryAPI";
import { UISchemaResponse } from "features/config-registry/types/configRegistryTypes";
import { StringRegistryTable } from "./StringRegistryTable";
import { StringTemplatesTab } from "./StringTemplatesTab";
import { StringRegistryFiltersDrawer } from "./StringRegistryFiltersDrawer";
import { StringRegistryItem } from "../types/stringRegistryTypes";

export const StringRegistryPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const [searchParams] = useSearchParams();

  const strings = useSelector(selectStringRegistryStrings);
  const pagination = useSelector(selectStringRegistryPagination);
  const filters = useSelector(selectStringRegistryFilters);
  const isLoading = useSelector(selectStringRegistryIsLoading);
  const error = useSelector(selectStringRegistryError);
  const hasActiveFilters = useSelector(selectHasActiveFilters);

  const [activeTab, setActiveTab] = useState<"strings" | "templates">("strings");

  const canCreate = rc.permissionsManager.canStringRegistryEdit();
  const canViewTemplates = rc.permissionsManager.canTemplateView();

  // Build tabs dynamically based on permissions
  const tabs = [
    { value: "strings", label: "Strings", disabled: false },
    ...(canViewTemplates ? [{ value: "templates", label: "Templates", disabled: false }] : []),
  ];

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "templates" && canViewTemplates) setActiveTab("templates");
    else if (tab === "strings") setActiveTab("strings");
  }, [searchParams, canViewTemplates]);

  // Fetch UI schema on mount for the filter drawer
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const schemaRes = await ConfigRegistryApis.getUISchema(rc);
        if (schemaRes.data) {
          const schemaData = schemaRes.data as any;
          const rawLookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];
          const normalizedLookupTypes = rawLookupTypes.map((lt: any) => ({
            ...lt,
            lookups: (lt.lookups || lt.options || []).map((opt: any) => ({
              ...opt,
              documentId: opt.documentId || opt.value,
              displayName: opt.displayName || opt.label || opt.code,
            })),
          }));
          const normalizedSchema: UISchemaResponse = {
            lookupTypes: normalizedLookupTypes,
            hierarchy: schemaData.hierarchy || {},
          };
          dispatch(setUISchema(normalizedSchema));
        }
      } catch (err) {
        // non-fatal
      }
    };
    fetchMetadata();
  }, [rc, dispatch]);

  // Monotonic id per request — out-of-order responses are dropped so a slow
  // page-N response can never overwrite a newer page's data/pagination
  const requestSeqRef = useRef(0);

  const fetchStrings = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    dispatch(setIsLoading(true));
    dispatch(setError(undefined));
    try {
      const response = await StringRegistryApis.getStrings(rc, {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search || undefined,
        verificationStatus: filters.verificationStatus || undefined,
        component: filters.component || undefined,
        section: filters.section || undefined
      });
      if (seq !== requestSeqRef.current) {
        return; // stale response — a newer request owns the table
      }
      if (response.error) {
        dispatch(setError(response.error.message || "Failed to fetch strings"));
      } else {
        const body = response as any;
        dispatch(setStrings(body.data || []));
        const meta = body.meta?.pagination;
        if (meta) {
          // page/pageSize are client-owned (what the user requested); only
          // total/pageCount come from the server. Writing meta.page back here
          // caused an infinite fetch loop when paginating faster than responses.
          dispatch(
            setPagination({
              page: pagination.page,
              pageSize: pagination.pageSize,
              pageCount: meta.pageCount || 1,
              total: meta.total || 0
            })
          );
        }
      }
    } catch (err: any) {
      if (seq === requestSeqRef.current) {
        dispatch(setError(err.message || "An error occurred"));
      }
    } finally {
      if (seq === requestSeqRef.current) {
        dispatch(setIsLoading(false));
      }
    }
  }, [
    rc,
    dispatch,
    pagination.page,
    pagination.pageSize,
    filters.search,
    filters.verificationStatus,
    filters.component,
    filters.section
  ]);

  useEffect(() => {
    fetchStrings();
  }, [fetchStrings]);

  const handlePageChange = (page: number, pageSize: number) => {
    dispatch(setPagination({ ...pagination, page, pageSize }));
  };

  const handleRowClick = (row: StringRegistryItem) => {
    navigate(`/cms/string-registry/${row.documentId}`);
  };

  const handleEdit = (row: StringRegistryItem) => {
    navigate(`/cms/string-registry/${row.documentId}?mode=edit`);
  };

  const handleCreate = () => {
    navigate("/cms/string-registry/new");
  };

  if (isLoading && strings.length === 0 && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <BglLoading />
      </Box>
    );
  }

  if (error && strings.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", mt: 4 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <BglPrimarySmallButton onClick={() => fetchStrings()} sx={{ mt: 2 }}>
          Retry
        </BglPrimarySmallButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 3 },
        height: "100%",
        overflow: "auto",
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          flex: 1,
          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.6)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2
        }}
      >
        {/* Row 1: Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 }
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <TuneIcon sx={{ fontSize: 28, color: "primary.main", mt: 0.5 }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
                String Registry
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                Global string metadata and per-locale values. Taxonomy uses the shared registry classification
                lookups. Locales follow Strapi i18n (same catalog as pilot supported locales).
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              width: { xs: "100%", md: "auto" },
              flexDirection: { xs: "column", sm: "row" }
            }}
          >
            <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
              <BglSecondarySmallButton
                startIcon={<FilterListIcon />}
                onClick={() => dispatch(toggleFilterPanel())}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Filters
              </BglSecondarySmallButton>
            </Badge>
            {canCreate && (
              <BglPrimarySmallButton
                startIcon={<AddIcon />}
                onClick={handleCreate}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Create new string
              </BglPrimarySmallButton>
            )}
          </Box>
        </Box>

        {/* Row 2: Tabs */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              navigate(
                newValue === "templates"
                  ? "/cms/string-registry?tab=templates"
                  : "/cms/string-registry?tab=strings",
                { replace: true }
              );
            }}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 500 }
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} disabled={tab.disabled} />
            ))}
          </Tabs>
        </Box>

        {/* Tab content */}
        {activeTab === "strings" && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <StringRegistryTable onPageChange={handlePageChange} onRowClick={handleRowClick} onEdit={handleEdit} />
          </Box>
        )}

        {activeTab === "templates" && canViewTemplates && (
          <Box sx={{ flex: 1 }}>
            <StringTemplatesTab />
          </Box>
        )}
      </Paper>

      {/* Filter Drawer */}
      <StringRegistryFiltersDrawer />
    </Box>
  );
};

export default StringRegistryPage;
